
import { useLocation } from "react-router-dom";
import { Helmet } from "react-helmet-async";

interface SEOProps {
  title?: string;
  description?: string;
  image?: string;
  article?: boolean;
  keywords?: string[];
  author?: string;
  publishedTime?: string;
  modifiedTime?: string;
  noindex?: boolean;
  nofollow?: boolean;
  children?: React.ReactNode;
}

export const SEO = ({
  title = "Seed Potato Organisation",
  description = "Run by growers for growers",
  image = "/lovable-uploads/2209c5e8-f8a9-4079-a2f4-f34eb5169b81.png",
  article = false,
  keywords = ["seed potato", "Scottish agriculture", "potato farming", "seed certification"],
  author = "Seed Potato Organisation",
  publishedTime,
  modifiedTime,
  noindex = false,
  nofollow = false,
  children
}: SEOProps) => {
  const { pathname } = useLocation();
  const baseUrl = "https://seedpotatoorganisation.com";
  const url = `${baseUrl}${pathname}`;
  const imageUrl = image.startsWith("http") ? image : `${baseUrl}${image}`;
  
  const fullTitle = title === "Seed Potato Organisation" 
    ? "Seed Potato Organisation | Supporting Scottish Seed Potato Industry" 
    : `${title} | Seed Potato Organisation`;

  const robots = [
    noindex ? 'noindex' : 'index',
    nofollow ? 'nofollow' : 'follow',
  ].join(', ');

  return (
    <Helmet>
      {/* Basic meta tags */}
      <title>{fullTitle}</title>
      <meta name="description" content={description} />
      <meta name="author" content={author} />
      <meta name="robots" content={robots} />
      
      {/* Keywords */}
      {keywords && <meta name="keywords" content={keywords.join(", ")} />}
      
      {/* Canonical URL */}
      <link rel="canonical" href={url} />
      
      {/* Open Graph */}
      <meta property="og:url" content={url} />
      <meta property="og:title" content={fullTitle} />
      <meta property="og:description" content={description} />
      <meta property="og:image" content={imageUrl} />
      <meta property="og:type" content={article ? "article" : "website"} />
      <meta property="og:site_name" content="Seed Potato Organisation" />
      
      {/* Open Graph Article specific */}
      {article && publishedTime && (
        <meta property="article:published_time" content={publishedTime} />
      )}
      {article && modifiedTime && (
        <meta property="article:modified_time" content={modifiedTime} />
      )}
      {article && author && (
        <meta property="article:author" content={author} />
      )}
      
      {/* Twitter */}
      <meta name="twitter:card" content="summary_large_image" />
      <meta name="twitter:title" content={fullTitle} />
      <meta name="twitter:description" content={description} />
      <meta name="twitter:image" content={imageUrl} />
      
      {/* Additional meta tags can be passed as children */}
      {children}
    </Helmet>
  );
};
